import { useState } from 'react'
import { Mail, Send } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  Button,
  Input,
  Label,
} from '@/components/ui'
import type { FamilyRole, InviteMemberRequest } from '@/types'

interface InviteMemberModalProps {
  open: boolean
  onClose: () => void
  onInvite: (data: InviteMemberRequest) => Promise<unknown>
  isInviting: boolean
}

const ROLE_OPTIONS: { value: FamilyRole; label: string; description: string }[] = [
  {
    value: 'PARENT',
    label: 'Parent',
    description: 'Family member being monitored for health and financial activity',
  },
  {
    value: 'CAREGIVER',
    label: 'Caregiver',
    description: 'Family member who can view and monitor others',
  },
  {
    value: 'VIEWER',
    label: 'Viewer',
    description: 'Read-only access to family information',
  },
]

export function InviteMemberModal({
  open,
  onClose,
  onInvite,
  isInviting,
}: InviteMemberModalProps) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<FamilyRole>('PARENT')
  const [error, setError] = useState('')

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    const trimmedEmail = email.trim().toLowerCase()

    if (!trimmedEmail) {
      setError('Please enter an email address')
      return
    }

    if (!validateEmail(trimmedEmail)) {
      setError('Please enter a valid email address')
      return
    }

    try {
      await onInvite({
        email: trimmedEmail,
        role,
      })
      // Reset form and close
      setEmail('')
      setRole('PARENT')
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation')
    }
  }


  const handleClose = () => {
    setEmail('')
    setRole('PARENT')
    setError('')
    onClose()
  }

  // Parent role doesn't need permission selection


  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Invite Family Member</SheetTitle>
          <SheetDescription>
            Send an invitation to add a new member to your family.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="mt-6 space-y-6">
          {/* Email */}
          <div>
            <Label htmlFor="invite-email">Email address</Label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                id="invite-email"
                type="email"
                placeholder="family@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setError('')
                }}
                className="pl-10"
              />
            </div>
            {error && <p className="mt-1 text-sm text-error-600">{error}</p>}
          </div>

          {/* Role selection */}
          <div>
            <Label>Role</Label>
            <div className="mt-2 space-y-2">
              {ROLE_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                    role === option.value
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="role"
                    value={option.value}
                    checked={role === option.value}
                    onChange={() => setRole(option.value)}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="font-medium text-gray-900">{option.label}</div>
                    <div className="text-sm text-gray-500">{option.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <p className="text-sm text-gray-500">Access is determined by the selected role. Viewers can view health; caregivers can view health and finances and manage alerts; parents manage their own data.</p>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={handleClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" disabled={isInviting} className="flex-1">
              <Send className="mr-2 h-4 w-4" />
              {isInviting ? 'Sending...' : 'Send Invitation'}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
